import { useCallback, useState } from 'react';
import { FileUp, Loader2, X } from 'lucide-react';
import { intakeFiles, type FileIntakeResult } from '../../lib/api';

interface MultiFileDropzoneProps {
  onComplete?: (results: FileIntakeResult[]) => void;
  conversationId?: string | null;
}

export default function MultiFileDropzone({ onComplete, conversationId }: MultiFileDropzoneProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<FileIntakeResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const addFiles = (incoming: FileList | File[]) => {
    setFiles((prev) => [...prev, ...Array.from(incoming)]);
    setPreview(null);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreview(null);
  };

  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] ?? '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const runIntake = useCallback(
    async (dryRun: boolean) => {
      if (files.length === 0) return;
      setLoading(true);
      try {
        const payload = await Promise.all(
          files.map(async (f) => ({
            filename: f.name,
            contentBase64: await toBase64(f),
            contentType: f.type || undefined,
          }))
        );
        const res = await intakeFiles(payload, dryRun, conversationId);
        if (res.ok && res.data) {
          setPreview(res.data.results);
          if (!dryRun) onComplete?.(res.data.results);
        }
      } finally {
        setLoading(false);
      }
    },
    [files, onComplete, conversationId]
  );

  return (
    <div className="rounded-xl border border-zt-border bg-zt-surface p-4">
      <h3 className="mb-3 text-sm font-medium text-white">File intake</h3>
      <div
        className={`rounded-lg border-2 border-dashed p-6 text-center transition ${
          dragging ? 'border-zt-primary bg-zt-primary/5' : 'border-zt-border'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <FileUp className="mx-auto mb-2 text-zt-muted" size={24} />
        <p className="text-sm text-zt-muted">Drop files to classify and route to oCIS</p>
        <label className="mt-2 inline-block cursor-pointer text-sm text-zt-primary hover:underline">
          Browse files
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {files.length > 0 && (
        <ul className="mt-3 space-y-1">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center justify-between text-sm text-slate-300">
              <span className="truncate">{f.name}</span>
              <button type="button" onClick={() => removeFile(i)} className="text-zt-muted hover:text-zt-danger">
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {preview && (
        <div className="mt-3 space-y-2 rounded-lg border border-zt-border bg-zt-bg p-3">
          <p className="text-xs font-medium text-zt-muted">Classification preview</p>
          {preview.map((r, i) => (
            <div key={i} className="text-xs text-slate-300">
              <span className="font-medium text-white">{r.filename}</span>
              <span className="mx-2 text-zt-muted">→</span>
              <span className="text-zt-primary">{r.category}</span>
              <span className="mx-2 text-zt-muted">@</span>
              <span>{r.destination}</span>
              {typeof r.confidence === 'number' && r.confidence < 0.55 && (
                <span className="ml-2 text-zt-muted">(low confidence — review)</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={files.length === 0 || loading}
          onClick={() => void runIntake(true)}
          className="rounded-lg border border-zt-border px-3 py-1.5 text-sm text-slate-200 hover:border-zt-primary disabled:opacity-40"
        >
          Preview
        </button>
        <button
          type="button"
          disabled={files.length === 0 || loading}
          onClick={() => void runIntake(false)}
          className="flex items-center gap-1 rounded-lg bg-zt-primary px-3 py-1.5 text-sm font-medium text-zt-bg disabled:opacity-40"
        >
          {loading && <Loader2 size={14} className="animate-spin" />}
          Confirm &amp; route
        </button>
      </div>
    </div>
  );
}
